import { useMemo, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { Repeat, ShoppingBag } from 'lucide-react'
import {
  Card,
  EmptyState,
  ErrorState,
  LoadMore,
  MoneyValue,
  Skeleton,
  StatusBadge,
} from '@shared/design-system/ui'
import {
  buildSalesRows,
  useSaleChargesQuery,
  filterRowsByView,
  getChargeStatusPresentation,
  SaleImpactCopy,
  saleIdLabel,
  subscriptionChargeExtraTags,
  subscriptionChargeLabel,
  summarizeSubscriptionCharge,
  individualSubscriptionCharges,
  summarizeJourney,
  summarizeJourneyStatuses,
} from '@entities/sale'
import type {
  SaleJourney,
  SaleListItem,
  SalesRow,
  SubscriptionCharge,
  SubscriptionGroup,
} from '@entities/sale'
import type { DashboardView } from '../api/dashboard.contract'
import {
  accumulatedHint,
  buyerCell,
  buyerName,
  buyerProduct,
  cycleList,
  cycleRow,
  cycleTitle,
  cycleTitleRow,
  funnelTag,
  expandCell,
  hideOnMobile,
  noWrapOnMobile,
  row,
  saleIdHint,
  sectionHeader,
  sectionTitle,
  table,
  td,
  th,
} from './dashboard.css'

const LATEST_SALES_LIMIT = 6

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function formatMonth(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(new Date(iso))
}

function itemCountLabel(count: number): string {
  return count === 1 ? '+1 item' : `+${count} itens`
}

function journeyHasCoupon(journey: SaleJourney): boolean {
  return [journey.sale, ...journey.children].some((charge) => charge.coupon !== undefined)
}

function subscriptionChargeHasCoupon(charge: SubscriptionCharge): boolean {
  return [charge.sale, ...charge.funnelChildren].some((sale) => sale.coupon !== undefined)
}

function chargeCountLabel(count: number): string {
  return count === 1 ? '1 cobrança' : `${count} cobranças`
}

function cycleLabel(charge: SubscriptionCharge, group: SubscriptionGroup): string {
  return subscriptionChargeLabel(charge, group)
}

function healthBadge(group: SubscriptionGroup): {
  label: string
  tone: 'success' | 'awaiting' | 'closed'
} {
  if (group.health === 'overdue') {
    return { label: 'Em atraso', tone: 'awaiting' }
  }
  if (group.health === 'ended') {
    return { label: 'Encerrada', tone: 'closed' }
  }
  return { label: 'Assinatura ativa', tone: 'success' }
}

export type LatestSalesProps = {
  sales: SaleListItem[]
  view: DashboardView
  onViewSale: (saleId: string, scope?: 'charge') => void
}

function latestRows(sales: SaleListItem[], view: DashboardView): SalesRow[] {
  return filterRowsByView(buildSalesRows(sales), view).slice(0, LATEST_SALES_LIMIT)
}

export function LatestSales({ sales, view, onViewSale }: LatestSalesProps) {
  const rows = useMemo(() => latestRows(sales, view), [sales, view])
  const [expandedId, setExpandedId] = useState<string | null>(null)

  function handleSaleKeyDown(event: KeyboardEvent<HTMLTableRowElement>, saleId: string) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }
    event.preventDefault()
    onViewSale(saleId)
  }

  return (
    <Card padding="md">
      <div className={sectionHeader}>
        <span className={sectionTitle}>
          {view === 'renewals' ? 'Últimas renovações' : 'Últimas vendas'}
        </span>
      </div>
      {rows.length === 0 ? (
        <EmptyState
          icon={<ShoppingBag size={22} />}
          title="Nenhuma venda recente"
          description="As vendas mais novas aparecem aqui quando chegarem."
        />
      ) : (
        <table className={table}>
          <thead>
            <tr>
              <th className={th}>Cliente</th>
              <th className={`${th} ${hideOnMobile}`}>Data</th>
              <th className={`${th} ${noWrapOnMobile}`}>Status</th>
              <th className={`${th} ${noWrapOnMobile}`} style={{ textAlign: 'right' }}>
                Valor
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) =>
              entry.type === 'subscription' ? (
                <SubscriptionLatestRow
                  key={`subscription:${entry.group.subscriptionId}`}
                  group={entry.group}
                  expanded={expandedId === entry.group.subscriptionId}
                  onToggle={() => {
                    setExpandedId((current) =>
                      current === entry.group.subscriptionId ? null : entry.group.subscriptionId,
                    )
                  }}
                  onViewSale={onViewSale}
                />
              ) : (
                <JourneyLatestRow
                  key={`journey:${entry.journey.sale.id}`}
                  journey={entry.journey}
                  onViewSale={onViewSale}
                  onKeyDown={handleSaleKeyDown}
                />
              ),
            )}
          </tbody>
        </table>
      )}
    </Card>
  )
}

function JourneyLatestRow({
  journey,
  onViewSale,
  onKeyDown,
}: {
  journey: SaleJourney
  onViewSale: (saleId: string, scope?: 'charge') => void
  onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>, saleId: string) => void
}) {
  const { sale } = journey
  const journeySummary = summarizeJourney(journey)
  const chargeSummary = summarizeJourneyStatuses(journey)
  const presentation = getChargeStatusPresentation(sale)
  const StatusIcon = presentation.icon
  const impact = {
    active: journeySummary.total,
    original: journeySummary.originalTotal,
    affected: journeySummary.affectedTotal,
    hasImpact: journeySummary.hasFinancialImpact,
  }

  return (
    <tr
      className={row}
      role="button"
      tabIndex={0}
      aria-label={`Abrir venda completa de ${sale.buyerName}: ${sale.productName}`}
      onClick={() => {
        onViewSale(sale.id)
      }}
      onKeyDown={(event) => {
        onKeyDown(event, sale.id)
      }}
    >
      <td className={td}>
        <div className={buyerCell}>
          <span className={buyerName}>{sale.buyerName}</span>
          <span className={buyerProduct}>
            {`${sale.productName}${journeySummary.itemCount > 0 ? ` · ${itemCountLabel(journeySummary.itemCount)} · ${journeySummary.separateChargeCount + 1} cobranças` : ''}${journeyHasCoupon(journey) ? ' · Cupom' : ''}`}
          </span>
          <span className={saleIdHint} title={sale.id}>
            {saleIdLabel(sale.id)}
          </span>
        </div>
      </td>
      <td className={`${td} ${hideOnMobile}`}>{formatDateTime(sale.createdAt)}</td>
      <td className={`${td} ${noWrapOnMobile}`}>
        <div className={buyerCell}>
          <StatusBadge
            tone={chargeSummary.mixed ? 'neutral' : presentation.tone}
            label={chargeSummary.mixed ? 'Situação mista' : presentation.label}
            size="sm"
            icon={<StatusIcon size={13} />}
          />
          {chargeSummary.chargeCount > 1 ? (
            <span className={buyerProduct}>
              {chargeSummary.chargeCount}{' '}
              {chargeSummary.chargeCount === 1 ? 'cobrança' : 'cobranças'} · {chargeSummary.label}
            </span>
          ) : null}
        </div>
      </td>
      <td className={`${td} ${noWrapOnMobile}`} style={{ textAlign: 'right' }}>
        {impact.hasImpact ? (
          <SaleImpactCopy
            active={impact.active}
            original={impact.original}
            affected={impact.affected}
            align="end"
          />
        ) : (
          <MoneyValue amount={impact.active.amount} currency={impact.active.currency} size="sm" />
        )}
      </td>
    </tr>
  )
}

function LazyLatestCycles({
  group,
  onViewSale,
}: {
  group: SubscriptionGroup
  onViewSale: (saleId: string, scope?: 'charge') => void
}) {
  const charges = useSaleChargesQuery(group.charges[0]!.sale.id)
  const items = charges.data?.pages.flatMap((page) => page.items) ?? []
  if (charges.isPending) return <Skeleton height={72} />
  if (charges.isError && items.length === 0)
    return <ErrorState onRetry={() => void charges.refetch()} />
  return (
    <>
      {items.map((sale) => (
        <LatestCycleRow
          key={sale.id}
          charge={{
            sale,
            cycleN: sale.subscriptionCycleN ?? 1,
            isRenewal: sale.isRenewal ?? false,
            funnelChildren: [],
          }}
          group={group}
          onViewSale={onViewSale}
        />
      ))}
      {charges.isFetchNextPageError ? (
        <ErrorState onRetry={() => void charges.fetchNextPage()} />
      ) : null}
      <LoadMore
        shown={items.length}
        hasMore={charges.hasNextPage}
        loading={charges.isFetchingNextPage}
        onClick={() => void charges.fetchNextPage()}
      />
    </>
  )
}

function SubscriptionLatestRow({
  group,
  expanded,
  onToggle,
  onViewSale,
}: {
  group: SubscriptionGroup
  expanded: boolean
  onToggle: () => void
  onViewSale: (saleId: string, scope?: 'charge') => void
}) {
  const badge = healthBadge(group)
  const loadedCharges = individualSubscriptionCharges(group.charges)

  return (
    <>
      <tr
        className={row}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`Assinatura de ${group.buyerName}: ${group.productName}, ${chargeCountLabel(group.chargeCount)}`}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onToggle()
          }
        }}
      >
        <td className={td}>
          <div className={buyerCell}>
            <span className={buyerName}>{group.buyerName}</span>
            <span className={buyerProduct}>
              {group.productName} · desde {formatMonth(group.firstAt)} ·{' '}
              {chargeCountLabel(group.chargeCount)}
              {group.charges.some(subscriptionChargeHasCoupon) ? ' · Cupom' : ''}
            </span>
          </div>
        </td>
        <td className={`${td} ${hideOnMobile}`}>{formatDateTime(group.lastAt)}</td>
        <td className={`${td} ${noWrapOnMobile}`}>
          <StatusBadge
            tone={badge.tone}
            label={badge.label}
            size="sm"
            icon={<Repeat size={13} />}
          />
        </td>
        <td className={`${td} ${noWrapOnMobile}`} style={{ textAlign: 'right' }}>
          {group.hasFinancialImpact ? (
            <SaleImpactCopy
              active={group.accumulated}
              original={group.originalAccumulated}
              affected={group.affectedAccumulated}
              align="end"
            />
          ) : (
            <MoneyValue
              amount={group.accumulated.amount}
              currency={group.accumulated.currency}
              size="sm"
            />
          )}
          <span className={accumulatedHint}>acumulado</span>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td className={expandCell} colSpan={4}>
            <div className={cycleList}>
              {group.chargeCount > loadedCharges.length ? (
                <LazyLatestCycles group={group} onViewSale={onViewSale} />
              ) : (
                loadedCharges.map((charge) => (
                  <LatestCycleRow
                    key={charge.sale.id}
                    charge={charge}
                    group={group}
                    onViewSale={onViewSale}
                  />
                ))
              )}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}

function LatestCycleRow({
  charge,
  group,
  onViewSale,
}: {
  charge: SubscriptionCharge
  group: SubscriptionGroup
  onViewSale: (saleId: string, scope?: 'charge') => void
}) {
  const presentation = getChargeStatusPresentation(charge.sale)
  const impact = summarizeSubscriptionCharge(charge)
  const extraTags = subscriptionChargeExtraTags(charge)
  return (
    <button
      key={charge.sale.id}
      type="button"
      className={cycleRow}
      onClick={() => {
        onViewSale(charge.sale.id, 'charge')
      }}
    >
      <span className={buyerCell}>
        <span className={cycleTitleRow}>
          <span className={cycleTitle}>{cycleLabel(charge, group)}</span>
          {subscriptionChargeHasCoupon(charge) ? <span className={funnelTag}>Cupom</span> : null}
          {extraTags.map((tag) => (
            <span key={tag} className={funnelTag}>
              {tag}
            </span>
          ))}
        </span>
        <span className={saleIdHint} title={charge.sale.id}>
          {saleIdLabel(charge.sale.id)}
        </span>
        <span className={buyerProduct}>
          {charge.sale.productName} · {presentation.label}
        </span>
      </span>
      {impact.hasImpact ? (
        <SaleImpactCopy
          active={impact.active}
          original={impact.original}
          affected={impact.affected}
          align="end"
        />
      ) : (
        <MoneyValue amount={impact.active.amount} currency={impact.active.currency} size="sm" />
      )}
    </button>
  )
}
